import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Shield, Star, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// New pricing plans
const PRICING_PLANS = {
  semester_ai: {
    id: 'semester_ai',
    name: 'Semester Pass - AI Course',
    price: 39,
    duration: '6 months',
    courses: ['AI'],
    features: [
      '10 AI Lessons with AI Teacher',
      '6 Months Access',
      'Self-paced: Move as fast as you want',
      '10 Downloadable Workbooks',
      'Standard Diploma upon completion'
    ],
    badge: null,
    color: '#06b6d4'
  },
  semester_coding: {
    id: 'semester_coding',
    name: 'Semester Pass - Coding Course',
    price: 39,
    duration: '6 months',
    courses: ['Coding'],
    features: [
      '10 Coding Lessons with AI Teacher',
      '6 Months Access',
      'Self-paced: Move as fast as you want',
      '10 Downloadable Workbooks',
      'Standard Diploma upon completion'
    ],
    badge: null,
    color: '#8b5cf6'
  },
  bundle: {
    id: 'bundle',
    name: 'Dual-Core Academy',
    price: 59,
    originalPrice: 78,
    duration: '12 months',
    courses: ['AI', 'Coding'],
    features: [
      'BOTH AI & Coding Courses (20 Lessons)',
      '12 Months Access - A full year!',
      'Priority AI: Faster response times',
      '20 Downloadable Workbooks',
      '"Honors" Gold Diploma',
      'Weekly Parent Progress Report'
    ],
    badge: '🏆 BEST VALUE - SAVE $19',
    color: '#f59e0b'
  },
  family: {
    id: 'family',
    name: 'Family Pack',
    price: 59,
    duration: '6 months each',
    courses: ['AI OR Coding'],
    features: [
      '2 Separate Student Accounts',
      'Choice of AI OR Coding per child',
      '6 Months Access for each child',
      'Individual Progress Tracking',
      'Personalized Diplomas for each'
    ],
    badge: '👨‍👩‍👧‍👦 2nd child 50% off!',
    color: '#22c55e'
  }
};

const PaymentCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [step, setStep] = useState(1); // 1: Select Plan, 2: Enter Details
  
  // For family plan
  const [studentName, setStudentName] = useState('');
  const [studentAge, setStudentAge] = useState('');
  const [secondStudentName, setSecondStudentName] = useState('');
  const [secondStudentAge, setSecondStudentAge] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(''); // for semester pass
  const [secondSelectedCourse, setSecondSelectedCourse] = useState(''); // for family plan

  useEffect(() => {
    // Check for plan from URL
    const urlPlan = searchParams.get('plan');
    if (urlPlan === 'semester') {
      setSelectedPlan('semester_ai');
    } else if (urlPlan === 'bundle') {
      setSelectedPlan('bundle');
    } else if (urlPlan === 'family') {
      setSelectedPlan('family');
    }
    
    // Check payment status
    checkPaymentStatus();
  }, []);

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/api/payments/status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        navigate('/payment-success?verified=true');
        return;
      } else if (response.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Failed to verify payment. Please contact support.');
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan) {
      setError('Please select a plan');
      return;
    }

    // Validate student info
    if (!studentName || !studentAge) {
      setError('Please enter student information');
      return;
    }

    if (selectedPlan === 'family' && (!secondStudentName || !secondStudentAge)) {
      setError('Please enter information for both students');
      return;
    }

    if ((selectedPlan === 'semester_ai' || selectedPlan === 'semester_coding') && !selectedCourse) {
      setError('Please select which course you want');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API}/api/payments/checkout`,
        {
          package_id: selectedPlan,
          student_name: studentName,
          student_age: parseInt(studentAge),
          second_student_name: secondStudentName || null,
          second_student_age: secondStudentAge ? parseInt(secondStudentAge) : null,
          selected_course: selectedCourse || null,
          second_selected_course: secondSelectedCourse || null,
          origin_url: originUrl
        },
        { withCredentials: true }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.response?.data?.detail || 'Failed to initiate checkout');
    } finally {
      setLoading(false);
    }
  };

  const plan = PRICING_PLANS[selectedPlan];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Home
          </button>
          <h1 style={{
            fontSize: '36px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '10px'
          }}>
            🎓 Choose Your Learning Adventure
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)' }}>
            Self-paced learning with our AI teacher - Master each lesson before moving on!
          </p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.5)',
            color: '#fca5a5',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        {step === 1 && (
          <>
            {/* Plan Selection */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {Object.values(PRICING_PLANS).map((planOption) => (
                <div
                  key={planOption.id}
                  onClick={() => setSelectedPlan(planOption.id)}
                  style={{
                    background: selectedPlan === planOption.id 
                      ? `linear-gradient(135deg, ${planOption.color}20 0%, ${planOption.color}10 100%)`
                      : 'rgba(255,255,255,0.05)',
                    border: selectedPlan === planOption.id 
                      ? `3px solid ${planOption.color}`
                      : '2px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    padding: '24px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    position: 'relative',
                    transform: selectedPlan === planOption.id ? 'scale(1.02)' : 'scale(1)'
                  }}
                >
                  {planOption.badge && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(135deg, ${planOption.color} 0%, ${planOption.color}cc 100%)`,
                      color: planOption.id === 'bundle' ? '#1a1a1a' : 'white',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}>
                      {planOption.badge}
                    </div>
                  )}

                  <div style={{ textAlign: 'center', marginBottom: '16px', paddingTop: planOption.badge ? '8px' : 0 }}>
                    <h3 style={{ color: 'white', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
                      {planOption.name}
                    </h3>
                    <div style={{ marginBottom: '8px' }}>
                      <span style={{ color: 'white', fontSize: '40px', fontWeight: 'bold' }}>
                        ${planOption.price}
                      </span>
                      {planOption.originalPrice && (
                        <span style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', marginLeft: '8px' }}>
                          ${planOption.originalPrice}
                        </span>
                      )}
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                      {planOption.duration} access
                    </p>
                  </div>

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {planOption.features.map((feature, idx) => (
                      <li key={idx} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        marginBottom: '10px',
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: '14px'
                      }}>
                        <CheckCircle style={{ width: '18px', height: '18px', color: planOption.color, flexShrink: 0, marginTop: '2px' }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {selectedPlan === planOption.id && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: `${planOption.color}30`,
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <span style={{ color: planOption.color, fontWeight: 'bold' }}>✓ Selected</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Continue Button */}
            {selectedPlan && (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setStep(2)}
                  style={{
                    padding: '16px 48px',
                    background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}cc 100%)`,
                    color: plan.id === 'bundle' ? '#1a1a1a' : 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: `0 8px 20px ${plan.color}40`
                  }}
                >
                  Continue with {plan.name} →
                </button>
              </div>
            )}
          </>
        )}

        {step === 2 && plan && (
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            background: 'rgba(255,255,255,0.05)',
            borderRadius: '16px',
            padding: '32px',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <button
              onClick={() => setStep(1)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                marginBottom: '20px'
              }}
            >
              ← Change Plan
            </button>

            <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '8px' }}>
              {plan.name}
            </h2>
            <p style={{ color: plan.color, fontSize: '32px', fontWeight: 'bold', marginBottom: '24px' }}>
              ${plan.price}
            </p>

            {/* Course Selection for Semester Pass */}
            {(selectedPlan === 'semester_ai' || selectedPlan === 'semester_coding') && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', color: 'white', marginBottom: '8px', fontWeight: '600' }}>
                  Select Course
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setSelectedCourse('AI');
                      setSelectedPlan('semester_ai');
                    }}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: selectedCourse === 'AI' ? '#06b6d4' : 'rgba(255,255,255,0.1)',
                      border: '2px solid',
                      borderColor: selectedCourse === 'AI' ? '#06b6d4' : 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    🤖 AI Course
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCourse('Coding');
                      setSelectedPlan('semester_coding');
                    }}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: selectedCourse === 'Coding' ? '#8b5cf6' : 'rgba(255,255,255,0.1)',
                      border: '2px solid',
                      borderColor: selectedCourse === 'Coding' ? '#8b5cf6' : 'rgba(255,255,255,0.2)',
                      borderRadius: '12px',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    💻 Coding Course
                  </button>
                </div>
              </div>
            )}

            {/* Student Information */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ color: 'white', marginBottom: '16px' }}>
                {selectedPlan === 'family' ? 'First Student' : 'Student'} Information
              </h3>
              <div style={{ display: 'grid', gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Student Name"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  style={{
                    padding: '14px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
                <input
                  type="number"
                  placeholder="Age (7-12)"
                  min="7"
                  max="12"
                  value={studentAge}
                  onChange={(e) => setStudentAge(e.target.value)}
                  style={{
                    padding: '14px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '16px'
                  }}
                />
              </div>
            </div>

            {/* Second Student for Family Plan */}
            {selectedPlan === 'family' && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: 'white', marginBottom: '16px' }}>Second Student Information</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <input
                    type="text"
                    placeholder="Second Student Name"
                    value={secondStudentName}
                    onChange={(e) => setSecondStudentName(e.target.value)}
                    style={{
                      padding: '14px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '16px'
                    }}
                  />
                  <input
                    type="number"
                    placeholder="Age (7-12)"
                    min="7"
                    max="12"
                    value={secondStudentAge}
                    onChange={(e) => setSecondStudentAge(e.target.value)}
                    style={{
                      padding: '14px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>
            )}

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={loading}
              style={{
                width: '100%',
                padding: '18px',
                background: loading ? 'rgba(255,255,255,0.2)' : `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}cc 100%)`,
                color: plan.id === 'bundle' ? '#1a1a1a' : 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: loading ? 'not-allowed' : 'pointer',
                marginBottom: '16px'
              }}
            >
              {loading ? 'Processing...' : `Pay $${plan.price} - Start Learning!`}
            </button>

            {/* Trust badges */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '24px',
              paddingTop: '16px',
              borderTop: '1px solid rgba(255,255,255,0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                <Shield style={{ width: '14px', height: '14px' }} />
                <span>Secure Payment</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                <Star style={{ width: '14px', height: '14px' }} />
                <span>Lifetime Workbooks</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCheckout;
